/**
 * Sync engine for visit lifecycle actions (#1471 → #1983).
 *
 * Reads from visit_sync_queue and dispatches each entry through the
 * op registry in `src/sync/writeOps.ts`. Each op declares its own
 * `sync()` (network call + response classification), so the engine is
 * op-agnostic — adding a new op is one registration, no engine change.
 *
 * Online-first: enqueue triggers an immediate attempt; failures are
 * retried on a 2-minute background tick and on `online` and
 * `visibilitychange` events. Per-op `maxRetries` governs how many
 * times a retriable failure is re-attempted before moving to terminal
 * 'failed' status.
 *
 * Always-on (default) after #1983 offline-first migration. The
 * `visitSyncEnabled` localStorage flag remains as an emergency kill
 * switch (set to "0" to disable per device).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  type HttpMethod,
  type SyncQueueRecord,
  deleteSyncQueueEntry,
  getAllSyncEntries,
  putSyncQueueEntry,
} from '../utils/visitDB';
import { STORES, put } from '../utils/indexedDB';
import { drainEventQueue } from '../services/eventSync';
import { requeueErroredEvents } from '../utils/eventLog';
import { getOp } from '../sync/writeOps';
import { selectDrainableRows, sweepOrphanedRows } from '../sync/writeQueueExt';
import { reconcileChildren } from '../sync/localIdReconciler';
import type { SyncResult } from '../sync/types';

// ---------- Public module API ----------

export interface NewSyncAction {
  action: string;
  endpoint: string;
  method: HttpMethod;
  payload?: unknown;
  priority?: number;
  maxRetries?: number;
  relatedTaskUUID?: string;
}

export interface SyncRunResult {
  attempted: number;
  succeeded: number;
  failed: number;
  skipped: number;
  /**
   * True when at least one op in this pass succeeded AND stamped a server
   * callLogId onto a parent visit (i.e. `reconcileChildren` ran). That is
   * the only kind of progress that can newly UNBLOCK a dependent op whose
   * `dependsOn` parent was previously unsynced. The drain cycle uses this
   * to decide whether another immediate pass could drain more rows (#1987,
   * GAP 1) instead of waiting ~2 min for the next background tick.
   */
  reconciledParent: boolean;
}

export function isSyncEngineEnabled(): boolean {
  // Always-on for authenticated users (#1983 offline-first migration).
  // The `visitSyncEnabled` localStorage flag is retained as an emergency
  // kill switch (admin sets to "0" to disable per device) but defaults
  // to enabled. The `?syncDebug=1` URL param still forces it on for
  // diagnostics.
  try {
    if (localStorage.getItem('visitSyncEnabled') === '0') return false;
  } catch {
    /* ignore */
  }
  return true;
}

/**
 * Enqueue an action for later sync. Fire-and-forget by design — callers
 * don't wait for the sync attempt. IDB auto-assigns the entry id.
 */
export async function enqueueSyncAction(action: NewSyncAction): Promise<void> {
  await putSyncQueueEntry({
    // `id` omitted — IDB auto-increment assigns one
    action: action.action,
    endpoint: action.endpoint,
    method: action.method,
    payload: action.payload ?? null,
    status: 'pending',
    priority: action.priority ?? 100,
    retries: 0,
    maxRetries: action.maxRetries ?? 3,
    createdAt: new Date().toISOString(),
    relatedTaskUUID: action.relatedTaskUUID,
  });

  // Hint any mounted engine to wake up and try right now.
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('rdm:sync-enqueued'));
  }
}

const RETRY_THROTTLE_MS = 10_000; // don't re-attempt the same entry within 10s

/**
 * A row left in status 'syncing' whose `lastAttempt` is older than this is
 * considered orphaned (the tab was closed / reloaded mid-fetch, so the op
 * never reached a terminal state). `selectDrainableRows` only returns
 * 'pending' rows, so an orphaned 'syncing' row would never retry. The
 * reaper resets such rows back to 'pending' (#1987, GAP 2).
 *
 * The threshold MUST exceed the longest realistic in-flight op duration so
 * we never reset a row that another concurrent drain pass is actively
 * processing — that pass stamps `lastAttempt = now` right before firing.
 */
const STALE_SYNCING_MS = 60_000;

/**
 * Reset rows stuck in 'syncing' back to 'pending' so they become drainable
 * again (#1987, GAP 2 — "stuck 'syncing' rows" reaper).
 *
 * Safety: a row is only reaped when its `lastAttempt` is missing or older
 * than STALE_SYNCING_MS. A pass that is currently processing a row stamps
 * `lastAttempt = now` immediately before flipping it to 'syncing', so an
 * actively-in-flight row is never reaped here. Returns the number of rows
 * reset (useful for diagnostics/tests).
 */
export async function reapStuckSyncingRows(staleMs = STALE_SYNCING_MS): Promise<number> {
  const all = (await getAllSyncEntries()) as SyncQueueRecord[];
  const now = Date.now();
  let reset = 0;
  for (const row of all) {
    if (row.status !== 'syncing') continue;
    const lastMs = row.lastAttempt ? new Date(row.lastAttempt).getTime() : 0;
    if (now - lastMs < staleMs) continue; // still potentially in-flight — leave it
    await put(STORES.VISIT_SYNC_QUEUE, { ...row, status: 'pending' });
    reset += 1;
  }
  return reset;
}

/**
 * Process all drainable entries (dependency-satisfied + pending) in
 * priority order. Dispatches each via the op registry, which means the
 * engine itself is op-agnostic — adding a new op is purely a matter of
 * registering it in writeOps.ts. See `src/sync/` for the op layer.
 *
 * Safe to call concurrently: the status='syncing' guard plus IDB
 * single-writer semantics prevent double-processing.
 */
export async function syncPendingEntries(): Promise<SyncRunResult> {
  const result: SyncRunResult = {
    attempted: 0,
    succeeded: 0,
    failed: 0,
    skipped: 0,
    reconciledParent: false,
  };

  const drainable = await selectDrainableRows();
  const now = Date.now();

  for (const entry of drainable) {
    // Throttle retries inside a single tick so we don't hammer a flaky endpoint.
    if (entry.retries > 0 && entry.lastAttempt) {
      const lastMs = new Date(entry.lastAttempt).getTime();
      if (now - lastMs < RETRY_THROTTLE_MS) {
        result.skipped += 1;
        continue;
      }
    }

    const op = getOp(entry.action);
    if (!op) {
      // Unknown op kind. Mark terminally failed so it surfaces in the
      // Sync Issues UI instead of silently lingering.
      await put(STORES.VISIT_SYNC_QUEUE, {
        ...entry,
        status: 'failed',
        failedReason: `No op registered for action="${entry.action}"`,
        lastAttempt: new Date().toISOString(),
      });
      result.failed += 1;
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('rdm:sync-failed'));
      }
      continue;
    }

    result.attempted += 1;

    // Mark syncing before firing so a concurrent caller skips this entry.
    await put(STORES.VISIT_SYNC_QUEUE, {
      ...entry,
      status: 'syncing',
      lastAttempt: new Date().toISOString(),
    });

    let syncResult: SyncResult;
    try {
      syncResult = await op.sync(entry);
    } catch (err) {
      syncResult = {
        status: 'failed',
        retriable: true,
        errorMessage: err instanceof Error ? err.message : String(err),
      };
    }

    if (syncResult.status === 'success') {
      // Stamp serverId onto the local domain row.
      try {
        await op.reconcile(entry, { calllogid: syncResult.serverId });
      } catch (err) {
        console.error(`[sync] reconcile failed for action=${entry.action}`, err);
      }
      // Cascade callLogId onto dependent rows (children, surveys, files).
      if (typeof syncResult.serverId === 'number' && entry.relatedTaskUUID) {
        try {
          await reconcileChildren(entry.relatedTaskUUID, syncResult.serverId);
        } catch (err) {
          console.error(`[sync] reconcileChildren failed for ${entry.relatedTaskUUID}`, err);
        }
        // A parent's callLogId is now stamped: dependent ops that were
        // gated on this parent (dependsOn === relatedTaskUUID) may have
        // become drainable. Signal the cycle to make another pass NOW
        // rather than waiting for the next background tick (#1987, GAP 1).
        result.reconciledParent = true;
      }
      if (typeof entry.id === 'number') {
        await deleteSyncQueueEntry(entry.id);
      }
      result.succeeded += 1;
      continue;
    }

    if (syncResult.status === 'pending') {
      // Op declined to fire — usually a parent dependency that hasn't
      // resolved at sync-time. Roll back to pending without bumping retries.
      await put(STORES.VISIT_SYNC_QUEUE, { ...entry, status: 'pending' });
      result.skipped += 1;
      continue;
    }

    // status === 'failed'
    const retriable = syncResult.retriable;
    const newRetries = entry.retries + 1;
    const exhausted = newRetries >= entry.maxRetries;

    await put(STORES.VISIT_SYNC_QUEUE, {
      ...entry,
      status: retriable && !exhausted ? 'pending' : 'failed',
      retries: newRetries,
      failedReason: syncResult.errorMessage,
      lastAttempt: new Date().toISOString(),
    });

    if (retriable && !exhausted) {
      result.attempted -= 1;
      result.skipped += 1;
    } else {
      result.failed += 1;
      // Tell the Sync Issues UI a new terminal failure exists so the
      // banner can appear without waiting for the next user action.
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('rdm:sync-failed'));
      }
    }
  }

  return result;
}

/**
 * Maximum drain passes per cycle. A guard against an unexpected
 * progress-without-termination loop (see reasoning below) — in practice a
 * cycle converges in 2 passes (check-in, then its dependents).
 */
const MAX_DRAIN_PASSES = 10;

/**
 * Module-scoped in-flight guard for `runDrainCycle` (#1987, GAP 3).
 *
 * `runDrainCycle` is invoked from many uncoordinated triggers — every mounted
 * `useSyncEngine`'s `syncNow`, the `online` / `visibilitychange` /
 * `rdm:sync-enqueued` listeners, the 2-min tick, and direct callers. The
 * per-hook `syncingRef` only serializes calls WITHIN a single hook instance;
 * it does NOT serialize across multiple mounts or direct callers. Two cycles
 * could therefore overlap, and because `syncPendingEntries` snapshots the
 * drainable set with `selectDrainableRows()` at the START of a pass, BOTH
 * cycles could select the same still-'pending' row and POST it before either
 * flipped it to 'syncing' — a select→mark race producing duplicate POSTs
 * (the Idempotency-Key dedupes the writes server-side, but the engine should
 * fire each op exactly once).
 *
 * Holding the single in-flight promise at module scope collapses all
 * concurrent callers onto ONE cycle: a second call while a cycle is running
 * returns the existing promise instead of starting a competing cycle.
 */
let drainInFlight: Promise<SyncRunResult> | null = null;

/**
 * Run a full drain cycle (#1987): reap stale 'syncing' rows, then drain
 * repeatedly until a pass makes no unblocking progress.
 *
 * Concurrency (GAP 3): module-serialized. If a cycle is already running, this
 * returns the in-flight promise rather than starting a second concurrent
 * cycle, so overlapping triggers/instances share one cycle and a 'pending'
 * row can never be selected twice before it is marked 'syncing'.
 *
 * GAP 1 fix — drain-until-no-progress: a single `syncPendingEntries()` pass
 * only attempts rows that were drainable at the START of the pass. When a
 * parent check-in reconciles mid-pass, its dependent ops (photo, survey,
 * checkout) become drainable but are NOT revisited until the next call.
 * Previously that "next call" was the ~2-min background tick. Here, if a
 * pass reported `reconciledParent`, we immediately run another pass so the
 * just-unblocked dependents drain in the SAME cycle.
 *
 * Loop-safety: we only continue while a pass set `reconciledParent === true`
 * AND made forward progress (`succeeded > 0`). Each successful op is DELETED
 * from the queue (or moved to terminal 'failed'), so the drainable set
 * strictly shrinks every pass — the loop cannot spin on the same rows.
 * `reconciledParent` can only be true finitely often because there is a
 * finite number of parent rows to reconcile. MAX_DRAIN_PASSES is a belt-and-
 * suspenders cap so a logic regression can never hang the engine; remaining
 * work simply waits for the next tick/event.
 */
export async function runDrainCycle(): Promise<SyncRunResult> {
  // Collapse concurrent callers onto the single in-flight cycle (GAP 3).
  if (drainInFlight) return drainInFlight;

  const cycle = (async (): Promise<SyncRunResult> => {
    // GAP 2: recover rows orphaned in 'syncing' by an interrupted prior cycle
    // before computing the drainable set, so they can participate this cycle.
    try {
      await reapStuckSyncingRows();
    } catch (err) {
      console.error('[sync] reapStuckSyncingRows failed', err);
    }

    const total: SyncRunResult = {
      attempted: 0,
      succeeded: 0,
      failed: 0,
      skipped: 0,
      reconciledParent: false,
    };

    for (let pass = 0; pass < MAX_DRAIN_PASSES; pass += 1) {
      const r = await syncPendingEntries();
      total.attempted += r.attempted;
      total.succeeded += r.succeeded;
      total.failed += r.failed;
      total.skipped += r.skipped;
      total.reconciledParent = total.reconciledParent || r.reconciledParent;

      // Re-drain only when this pass actually unblocked dependents: a parent
      // was reconciled AND at least one op succeeded (strictly shrinking the
      // queue). Otherwise stop — another pass would attempt the same rows.
      if (!(r.reconciledParent && r.succeeded > 0)) break;
    }

    // After draining, fail any rows that can NEVER drain — a dependent whose
    // parent visit has no calllogid and no live parent op left to mint one.
    // Left pending they'd stick the "N actions to sync" banner forever and
    // make "Sync Now" a no-op; failing them surfaces Retry/Discard on the
    // Sync Issues page instead (diag #43).
    try {
      total.failed += await sweepOrphanedRows();
    } catch (err) {
      console.error('[sync] sweepOrphanedRows failed', err);
    }

    return total;
  })();

  drainInFlight = cycle;
  try {
    return await cycle;
  } finally {
    // Release the guard so the NEXT trigger starts a fresh cycle. Only clear
    // if we still own the slot (defensive — assignment above is synchronous,
    // so this is always our own promise, but keep it robust).
    if (drainInFlight === cycle) drainInFlight = null;
    // Self-sustaining retry (#1987): a row left 'pending' after a retriable
    // failure is throttled by RETRY_THROTTLE_MS and would otherwise sit idle
    // until an external trigger or the slow background tick. (Re)arm a single
    // debounced timer to wake the engine once the throttle elapses so such
    // rows get their next attempt promptly. Runs after the guard is released
    // so the scheduled cycle can actually start.
    void scheduleRetryIfPending();
  }
}

// ---------- Self-scheduled retry loop (#1987) ----------

/**
 * Single module-level timer that wakes the engine after the retry throttle so
 * rows left 'pending' by a retriable failure get their next attempt without an
 * external trigger (online / visibilitychange / rdm:sync-enqueued) or the slow
 * 2-min background tick.
 *
 * Shared across all hook mounts and direct callers — there is exactly ONE
 * timer at module scope so overlapping triggers/instances can never stack
 * multiple wakeups. `scheduleRetryIfPending` clears any pending timer before
 * (re)arming, so it is debounced rather than multiplied.
 */
let retryTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Clear the self-retry timer (idempotent). Used on (re)arm, on unmount of the
 * last engine, and by tests to reset module state between cases.
 */
export function clearRetryTimer(): void {
  if (retryTimer !== null) {
    clearTimeout(retryTimer);
    retryTimer = null;
  }
}

/**
 * A row is RETRIABLE-pending when it is queued ('pending') and still has
 * attempts left (retries < maxRetries). Such a row failed retriably (or hasn't
 * fired yet) and will become eligible again once RETRY_THROTTLE_MS elapses.
 * Rows that are TERMINAL-failed (status 'failed', or retries >= maxRetries) are
 * excluded so the loop stops instead of spinning forever.
 */
function isRetriablePending(row: SyncQueueRecord): boolean {
  return row.status === 'pending' && row.retries < row.maxRetries;
}

/**
 * After a drain cycle, inspect the queue. If any RETRIABLE-pending work remains,
 * (re)arm the single module-level timer to fire ~RETRY_THROTTLE_MS from the
 * soonest per-row retry-eligible time. When it fires it calls `runDrainCycle()`
 * (which, via the module guard, won't run concurrently) and that cycle in turn
 * re-arms the timer if work still remains — a self-sustaining, debounced loop
 * that stops as soon as everything drains or all remaining rows are terminal.
 *
 * The timer only WAKES the engine; `selectDrainableRows` + RETRY_THROTTLE_MS
 * still govern what actually re-POSTs, so retries are neither double-counted nor
 * fired before the throttle elapses.
 */
async function scheduleRetryIfPending(): Promise<void> {
  let rows: SyncQueueRecord[];
  try {
    rows = (await getAllSyncEntries()) as SyncQueueRecord[];
  } catch (err) {
    console.error('[sync] scheduleRetryIfPending: read failed', err);
    return;
  }

  const retriable = rows.filter(isRetriablePending);
  if (retriable.length === 0) {
    // Nothing left to retry — stop the loop (do not re-arm).
    clearRetryTimer();
    return;
  }

  // Wake once the soonest row clears its throttle. A row that already failed
  // once (retries > 0) must wait RETRY_THROTTLE_MS from its lastAttempt; a row
  // that hasn't attempted yet is immediately eligible, so fall back to the full
  // throttle as a minimal debounce.
  const now = Date.now();
  let soonestDelay = RETRY_THROTTLE_MS;
  for (const row of retriable) {
    if (row.retries > 0 && row.lastAttempt) {
      const eligibleAt = new Date(row.lastAttempt).getTime() + RETRY_THROTTLE_MS;
      soonestDelay = Math.min(soonestDelay, Math.max(0, eligibleAt - now));
    } else {
      soonestDelay = Math.min(soonestDelay, RETRY_THROTTLE_MS);
    }
  }

  // Debounce: clear any existing timer before (re)arming so we never stack.
  clearRetryTimer();
  retryTimer = setTimeout(() => {
    retryTimer = null;
    void runDrainCycle().catch(() => undefined);
  }, soonestDelay);
}

// ---------- React hook ----------

export interface UseSyncEngineResult {
  pendingCount: number;
  failedCount: number;
  isSyncing: boolean;
  lastSyncAt: string | null;
  lastResult: SyncRunResult | null;
  entries: SyncQueueRecord[]; // for diagnostic panel
  refresh: () => Promise<void>;
  syncNow: () => Promise<SyncRunResult>;
  deleteEntry: (id: number) => Promise<void>;
  clearAll: () => Promise<void>;
}

const BACKGROUND_TICK_MS = 2 * 60 * 1000;

/**
 * Number of currently-mounted `useSyncEngine` hooks. The self-retry timer is
 * module-level (shared), so a single hook unmounting must NOT cancel a retry
 * the rest of the app still depends on. We only clear the timer when the LAST
 * mount unwinds (count hits 0), keeping the loop alive while any consumer is
 * present and avoiding leaking a timer once none are.
 */
let mountedEngineCount = 0;

export function useSyncEngine(): UseSyncEngineResult {
  const [entries, setEntries] = useState<SyncQueueRecord[]>([]);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncAt, setLastSyncAt] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<SyncRunResult | null>(null);
  const syncingRef = useRef(false); // guards against concurrent syncNow calls in the same hook

  const refresh = useCallback(async () => {
    const all = await getAllSyncEntries();
    all.sort((a, b) => {
      if (a.priority !== b.priority) return a.priority - b.priority;
      return a.createdAt.localeCompare(b.createdAt);
    });
    setEntries(all);
  }, []);

  const syncNow = useCallback(async (): Promise<SyncRunResult> => {
    if (syncingRef.current) {
      return { attempted: 0, succeeded: 0, failed: 0, skipped: 0, reconciledParent: false };
    }
    syncingRef.current = true;
    setIsSyncing(true);
    try {
      // runDrainCycle (not syncPendingEntries) so a check-in that reconciles
      // mid-cycle immediately drains its dependents, and any row orphaned in
      // 'syncing' is reaped first (#1987, GAP 1 + GAP 2).
      const result = await runDrainCycle();
      // Also flush the EVENT-log queue (a separate store from the visit sync
      // queue). Re-queue any parked 'error' rows first so a transient failure
      // that stranded them recovers on the button the rep already presses —
      // otherwise they're unreachable and only manual IndexedDB deletion clears
      // them. Best-effort: never let it fail the visit-queue sync.
      try {
        await requeueErroredEvents();
        await drainEventQueue();
      } catch (err) {
        console.warn('[useSyncEngine] event-queue flush on Sync Now failed (non-fatal):', err);
      }
      setLastResult(result);
      setLastSyncAt(new Date().toISOString());
      await refresh();
      return result;
    } finally {
      syncingRef.current = false;
      setIsSyncing(false);
    }
  }, [refresh]);

  const deleteEntry = useCallback(
    async (id: number) => {
      await deleteSyncQueueEntry(id);
      await refresh();
    },
    [refresh]
  );

  const clearAll = useCallback(async () => {
    const all = await getAllSyncEntries();
    for (const e of all) {
      await deleteSyncQueueEntry(e.id);
    }
    await refresh();
  }, [refresh]);

  // Lifecycle safety for the module-level self-retry timer (#1987): track how
  // many engines are mounted so the timer is only cleared when the last one
  // unmounts. A single mount unmounting must not cancel a retry the rest of the
  // app still relies on, and the timer must not leak once no consumer remains.
  useEffect(() => {
    mountedEngineCount += 1;
    return () => {
      mountedEngineCount -= 1;
      if (mountedEngineCount <= 0) {
        mountedEngineCount = 0;
        clearRetryTimer();
      }
    };
  }, []);

  // Initial load. Reap any row orphaned in 'syncing' by a previous session
  // that closed mid-fetch BEFORE the first refresh, so the diagnostic counts
  // are accurate and the row is drainable on the next event/tick (#1987,
  // GAP 2). reapStuckSyncingRows is timestamp-guarded, so this is a no-op for
  // rows a concurrently-running cycle is actively processing.
  useEffect(() => {
    reapStuckSyncingRows()
      .catch((e) => console.warn('[useSyncEngine] startup reap failed', e))
      .finally(() => {
        refresh().catch((e) => console.warn('[useSyncEngine] initial refresh failed', e));
      });
  }, [refresh]);

  // Event-driven triggers
  useEffect(() => {
    const onOnline = () => {
      if (isSyncEngineEnabled()) syncNow().catch(() => undefined);
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isSyncEngineEnabled()) {
        syncNow().catch(() => undefined);
      }
    };
    const onEnqueued = () => {
      if (isSyncEngineEnabled()) syncNow().catch(() => undefined);
      else refresh().catch(() => undefined); // still update UI count for diagnostic panel
    };

    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('rdm:sync-enqueued', onEnqueued);

    return () => {
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('rdm:sync-enqueued', onEnqueued);
    };
  }, [syncNow, refresh]);

  // Background tick (feature-flagged)
  useEffect(() => {
    if (!isSyncEngineEnabled()) return;
    const interval = setInterval(() => {
      if (!navigator.onLine) return;
      syncNow().catch(() => undefined);
    }, BACKGROUND_TICK_MS);
    return () => clearInterval(interval);
  }, [syncNow]);

  const pendingCount = entries.filter((e) => e.status === 'pending').length;
  const failedCount = entries.filter((e) => e.status === 'failed').length;

  return {
    pendingCount,
    failedCount,
    isSyncing,
    lastSyncAt,
    lastResult,
    entries,
    refresh,
    syncNow,
    deleteEntry,
    clearAll,
  };
}
